import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import { hlsFetchSetup } from '@lib/hls/fetchSetup';
import { isHlsSupported } from '@lib/hls/support';
import { getAccessTokenFromUrl, getLibraryIdFromPreviewUrl } from '@lib/hooks/preview/preview';
import { MB_TO_BYTES } from '@lib/utils/units';
import { CircularProgress, Skeleton, Tooltip } from '@mui/material';
import { libraryPreview } from '@stores/Library/LibraryPreview';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import Hls from 'hls.js';
import { observer } from 'mobx-react';
import PQueue from 'p-queue';
import React, { forwardRef, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { throttle } from 'throttle-debounce';
import css from './VideoPreview.module.scss';

const DEFAULT_MAX_MAX_BUFFER_LENGTH = 30;

// Circuit breaker to prevent infinite retry loops
const circuitBreaker = new Map<
  string,
  { failures: number; lastFailure: number; isOpen: boolean }
>();
const CIRCUIT_BREAKER_THRESHOLD = 5; // Max failures before opening circuit
const CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds before attempting again

// Periodic cleanup to prevent memory leaks
const CIRCUIT_BREAKER_CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
setInterval(() => {
  const now = Date.now();
  for (const [key, state] of circuitBreaker.entries()) {
    if (now - state.lastFailure > CIRCUIT_BREAKER_TIMEOUT * 2) {
      circuitBreaker.delete(key);
    }
  }
}, CIRCUIT_BREAKER_CLEANUP_INTERVAL);

// Video state machine types
type VideoState =
  | 'IDLE'
  | 'LOADING'
  | 'PLAYING_AUTOPLAY'
  | 'PLAYING_HOVER'
  | 'PAUSED'
  | 'ERROR'
  | 'BUFFERING';

type VideoAction =
  | { type: 'START_LOADING' }
  | { type: 'LOADING_COMPLETE' }
  | { type: 'START_AUTOPLAY' }
  | { type: 'START_HOVER_PLAY' }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'ERROR' }
  | { type: 'BUFFERING_START' }
  | { type: 'BUFFERING_END' }
  | { type: 'RESET' }
  | { type: 'SET_MOUSE_OVER'; isMouseOver: boolean };

interface VideoStateMachine {
  state: VideoState;
  isMouseOver: boolean;
  retryCount: number;
}

// State machine reducer
function videoStateReducer(state: VideoStateMachine, action: VideoAction): VideoStateMachine {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, state: 'LOADING' };

    case 'LOADING_COMPLETE':
      return { ...state, state: 'IDLE' };

    case 'START_AUTOPLAY':
      return { ...state, state: 'PLAYING_AUTOPLAY' };

    case 'START_HOVER_PLAY':
      return { ...state, state: 'PLAYING_HOVER' };

    case 'PAUSE':
      return { ...state, state: 'PAUSED' };

    case 'RESUME':
      // Resume to appropriate playing state based on context
      return {
        ...state,
        state: state.isMouseOver ? 'PLAYING_HOVER' : 'PLAYING_AUTOPLAY',
      };

    case 'ERROR':
      return { ...state, state: 'ERROR' };

    case 'BUFFERING_START':
      return { ...state, state: 'BUFFERING' };

    case 'BUFFERING_END':
      // Return to previous playing state
      return {
        ...state,
        state: state.isMouseOver ? 'PLAYING_HOVER' : 'PLAYING_AUTOPLAY',
      };

    case 'RESET':
      return { ...state, state: 'IDLE', retryCount: state.retryCount + 1 };

    case 'SET_MOUSE_OVER':
      return { ...state, isMouseOver: action.isMouseOver };

    default:
      return state;
  }
}

export type VideoPreviewProps = {
  src: string;
  start?: number;
  end?: number;

  duration?: number;
  frameCount?: number;

  className?: string;
  style?: React.CSSProperties;

  videoClassName?: string;
  videoStyle?: React.CSSProperties;

  children?: React.ReactNode;

  controls?: boolean;
  autoPlay?: boolean;

  onClick?: () => void;

  thumbnail?: string;
  thumbnailToken?: string | null;

  showThumbnailWhenPaused?: boolean;

  maxMaxBufferLength?: number;

  isMoment?: boolean;

  /** When true, disables hover/tap-to-play behavior (mobile autoplay off). */
  disableHoverPlay?: boolean;

  priority?: number;

  // Library ID for authentication (preferred over URL extraction)
  libraryId?: string;

  /** Delay (ms) before starting HLS load. If the component unmounts before the
   *  delay elapses, the load is skipped entirely. Useful for avoiding wasted HLS
   *  loads on cards that are only briefly visible during fast scrolling. */
  loadDelay?: number;

  /** When true, the video is being played by an external controller (e.g. mobile
   *  center-line autoplay). Bypasses hover-based visual gating: shows the video
   *  over the thumbnail and removes the paused CSS class, without requiring
   *  isMouseOver to be true. The external controller is responsible for calling
   *  video.play() / video.pause() directly. */
  externallyPlaying?: boolean;
};

const isMobileDevice = () => typeof window !== 'undefined' && window.innerWidth <= 768;
const loadQueue = new PQueue({ concurrency: isMobileDevice() ? 2 : 5, autoStart: true });
// @ts-ignore
window.videoq = loadQueue;
// todo: make progress bar appear on phone, find a way to scrub video on phone
export const VideoPreview = observer(
  forwardRef<HTMLVideoElement, VideoPreviewProps>(function VideoPreview(props, ref) {
    const {
      src,
      style,
      className,
      videoClassName,
      videoStyle,
      children,
      onClick,
      controls = false,
      autoPlay = false,
      duration,
      frameCount,
      thumbnail,
      thumbnailToken,
      showThumbnailWhenPaused = false,
      maxMaxBufferLength,
      isMoment = false,
      disableHoverPlay = false,
      priority = 0,
      libraryId: libraryIdProp,
      loadDelay = 0,
      externallyPlaying = false,
    } = props;

    const start = props.start == undefined ? 0 : props.start;
    const end = props.end == undefined ? duration || 1 : props.end || 1;
    const frameInterval = duration && frameCount ? duration / frameCount : 0.042;

    // adding small offsets
    // fixes for floating point imprecision causing infinite seeking when timestamps don't align nicely
    const actualStart = Math.round((start + 0.001) * 1e6) / 1e6;
    const actualEnd = Math.round((end - frameInterval - 0.001) * 1e6) / 1e6;

    // this is an invalid state, where start > end.
    // it means that the clips is very short, cannot be played reliably without going over the boundaries
    // so we prevent playback all together
    // todo: we should also prevent the playback when there are just a few frames, it doesn't make sense
    const videoUnplayable = actualStart > actualEnd;

    const timeRange = actualEnd - actualStart;

    const [video, setVideoRef] = useState<HTMLVideoElement | null>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountRef = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(true);
    const [offscreenDestroyed, setOffscreenDestroyed] = useState(false);

    // State machine for video state management
    const [videoState, dispatch] = useReducer(videoStateReducer, {
      state: 'IDLE',
      isMouseOver: false,
      retryCount: 0,
    });

    // Refs for values read by async callbacks (closures, timers, event handlers)
    // so they always see the latest value instead of a stale closure capture.
    const videoShouldAutoplayRef = useRef(autoPlay);

    const uiPreferences = useUIPreferences();
    // For moments, only the moment-specific autoplay toggle matters.
    // For non-moments (library grid), use the general autoplayVideos setting.
    // Moments always respond to hover (play on hover even when autoplay is off).
    // disableHoverPlay overrides this for mobile when autoplay is off.
    const isAutoplayOnHoverEnabled = disableHoverPlay ? false : autoPlay || isMoment;
    // For moments with autoplay enabled, don't use hover-based control
    // For moments with autoplay disabled, use hover-based control
    // For non-moments, use hover-based control based on autoPlay prop
    const shouldUseHoverControl = disableHoverPlay ? false : !isMoment || !autoPlay;

    // Keep ref in sync so async callbacks always read the latest value.
    videoShouldAutoplayRef.current = autoPlay;

    // Determine if video should be paused based on state machine.
    // externallyPlaying bypasses this — the external controller manages play state.
    const isPausedState =
      !externallyPlaying &&
      (videoState.state === 'PAUSED' ||
        (shouldUseHoverControl && videoState.state === 'IDLE' && !videoState.isMouseOver));

    // Muting logic: mute when sound is disabled, or when autoplaying and not hovered.
    // The sound toggle only matters when the user is actively hovering a card.
    const shouldMute = !uiPreferences.soundEnabled || (autoPlay && !videoState.isMouseOver);

    // Handle ref forwarding
    const setVideoRefWithForwarding = useCallback(
      (videoElement: HTMLVideoElement | null) => {
        setVideoRef(videoElement);
        // Forward the ref to the parent component
        if (typeof ref === 'function') {
          ref(videoElement);
        } else if (ref) {
          ref.current = videoElement;
        }
      },
      [ref],
    );

    // Handle autoplay state changes
    useEffect(() => {
      if (!video) return;

      // Handle autoplay for moments
      if (isMoment && !videoUnplayable) {
        if (autoPlay && !shouldUseHoverControl) {
          // Start autoplay when enabled and not using hover control
          dispatch({ type: 'START_AUTOPLAY' });
          video.play().catch(console.error);
        }
      }
    }, [autoPlay, videoUnplayable, isMoment, shouldUseHoverControl]);

    // Intersection Observer to pause video when off-screen
    useEffect(() => {
      if (!containerRef.current) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsVisible(entry.isIntersecting);

            // Pause video when it goes off-screen
            if (!entry.isIntersecting && video && !video.paused) {
              dispatch({ type: 'PAUSE' });
              video.pause();

              // Also dispatch mouse leave to reset hover state
              dispatch({ type: 'SET_MOUSE_OVER', isMouseOver: false });
            }
          });
        },
        {
          // Trigger when video is completely out of view
          threshold: 0,
          // Add some margin to pause slightly before fully off-screen
          rootMargin: '-50px',
        },
      );

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }, [video]);

    // Separate observer for HLS destruction — uses a generous margin so cards
    // are only destroyed once they're well past the viewport edge.
    useEffect(() => {
      if (!containerRef.current) return;

      let desktopDestroyTimer: NodeJS.Timeout | null = null;
      const DESKTOP_DESTROY_DELAY = 30_000; // 30s grace period on desktop

      const destroyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (desktopDestroyTimer) {
                clearTimeout(desktopDestroyTimer);
                desktopDestroyTimer = null;
              }
              setOffscreenDestroyed(false);
            } else if (isMobileDevice()) {
              setOffscreenDestroyed(true);
            } else {
              desktopDestroyTimer = setTimeout(() => {
                setOffscreenDestroyed(true);
              }, DESKTOP_DESTROY_DELAY);
            }
          });
        },
        {
          threshold: 0,
          // 200px buffer — card must be 200px past the viewport before destruction
          rootMargin: '200px',
        },
      );

      destroyObserver.observe(containerRef.current);

      return () => {
        if (desktopDestroyTimer) clearTimeout(desktopDestroyTimer);
        destroyObserver.disconnect();
      };
    }, [video]);

    // Proactively refresh tokens when tab becomes visible (prevents 401s after long inactivity)
    useEffect(() => {
      if (!libraryIdProp) return;

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          const cachedToken = libraryPreview._previewTokens[libraryIdProp];
          if (cachedToken) {
            const timeUntilExpiry = cachedToken.expiresAt - Date.now();
            // Refresh if expires in < 5 minutes
            if (timeUntilExpiry < 5 * 60 * 1000) {
              libraryPreview.getValidPreviewToken(libraryIdProp).catch(console.warn);
            }
          }
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [libraryIdProp]);

    const updateProgress = useCallback(
      throttle(33, (ts: number) => {
        const progressBar = progressBarRef.current;
        if (progressBar) {
          const progress = (ts - actualStart) / timeRange;
          progressBar.style.transform = `translateX(-${(1 - progress) * 100}%)`;
        }
      }),
      [start, timeRange],
    );

    const seek = (ts: number) => {
      if (video) {
        video.currentTime = ts;
      }
    };

    // Track video dimensions and loading state once metadata is loaded
    useEffect(() => {
      if (!video) return;

      const onLoadedData = () => {
        setVideoSize({
          width: video.videoWidth,
          height: video.videoHeight,
        });
        dispatch({ type: 'LOADING_COMPLETE' });
        video.currentTime = actualStart;
        // Use ref to read the latest autoplay value (this handler fires asynchronously).
        if (videoShouldAutoplayRef.current && !videoUnplayable) {
          if (videoState.isMouseOver) {
            dispatch({ type: 'START_HOVER_PLAY' });
          } else {
            dispatch({ type: 'START_AUTOPLAY' });
          }
          video.play().catch(console.error);
        }
      };

      const onWaiting = () => {
        if (!video.paused) {
          dispatch({ type: 'BUFFERING_START' });
        }
      };

      const onPlaying = () => {
        dispatch({ type: 'BUFFERING_END' });
      };

      const onPause = () => {
        dispatch({ type: 'PAUSE' });
      };

      video.addEventListener('loadeddata', onLoadedData);
      video.addEventListener('waiting', onWaiting);
      video.addEventListener('playing', onPlaying);
      video.addEventListener('pause', onPause);

      return () => {
        video.removeEventListener('loadeddata', onLoadedData);
        video.removeEventListener('waiting', onWaiting);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('pause', onPause);
      };
    }, [video, isMoment, autoPlay, videoUnplayable, actualStart]);

    // HLS and frame callback effect
    useEffect(() => {
      if (!video || (!isMoment && !autoPlay) || !src) return;
      // Skip HLS init when off-screen — cleanup from the previous run already
      // destroyed the instance, so we just wait until the card scrolls back.
      if (offscreenDestroyed) return;

      let hls: Hls | null = null;
      let nextFrameCallbackRef: number | null = null;
      let frameCallbackTimer: NodeJS.Timeout | null = null;
      let loadDelayTimer: NodeJS.Timeout | null = null;
      const abortController = new AbortController();
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second

      const initHls = () => {
        // Reset retry count for this effect run
        retryCountRef.current = 0;

        // Reset error state when starting new load
        dispatch({ type: 'START_LOADING' });
        // Clear any pending error timeout
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }

        const attemptHlsLoad = async (signal: AbortSignal): Promise<void> => {
          if (signal.aborted) {
            return;
          }

          // Get access token for the preview URL
          // Prefer prop over URL extraction for reliability
          const libraryId = libraryIdProp || getLibraryIdFromPreviewUrl(src);

          if (!libraryId) {
            console.warn(
              '[VideoPreview] Could not determine libraryId for HLS.js. ' +
                'Pass libraryId as a prop or ensure the URL contains a valid library path.',
              { src },
            );
            dispatch({ type: 'ERROR' });
            return;
          }

          // Uses library-scoped preview tokens with caching (reduces API load during scrolling).
          // The backend accepts both preview tokens and user tokens - we use preview tokens here
          // for performance (cached) and to handle token expiration in long-lived sessions.
          //
          // Token lifecycle:
          //   1. Fetch and cache the preview token
          //   2. Pass a function (not value) to HLS that reads from cache on each request
          //   3. On 401 errors, refresh the token and retry
          //   4. Proactively refresh tokens when tab becomes visible (see useEffect below)

          // Fetch and cache the preview token
          const accessToken = await libraryPreview
            .getValidPreviewToken(libraryId)
            .catch((error) => {
              console.error('Failed to get preview token:', error);
              dispatch({ type: 'ERROR' });
              return null;
            });

          if (!accessToken) return;

          // Create dynamic token getter that reads from cache (allows token rotation)
          const tokenGetter = () => libraryPreview.getCachedPreviewToken(libraryId);

          hls = getHlsInstance(actualStart, timeRange, maxMaxBufferLength, src, tokenGetter) as Hls;

          // Set up frame callback when HLS manifest is parsed
          if ('requestVideoFrameCallback' in HTMLVideoElement.prototype && video && isMoment) {
            hls!.on(Hls.Events.MANIFEST_PARSED, () => {
              // Clear any existing timer
              if (frameCallbackTimer) {
                clearTimeout(frameCallbackTimer);
                frameCallbackTimer = null;
              }

              // Set up timer — only play if autoplay is actually on.
              // Without this guard the play()-then-pause() hack races and
              // leaves videos playing when autoplay is off.
              frameCallbackTimer = setTimeout(async () => {
                if (video && videoShouldAutoplayRef.current) {
                  try {
                    await video.play();
                  } catch (error) {
                    console.warn('Failed to play video for frame callback initialization:', error);
                  }
                }
                frameCallbackTimer = null;
              }, 100);

              const frameCallback = (now: number, metadata: VideoFrameCallbackMetadata) => {
                // Frame callback fired - clear the timer
                if (frameCallbackTimer) {
                  clearTimeout(frameCallbackTimer);
                  frameCallbackTimer = null;
                }

                updateProgress(metadata.mediaTime);
                const fixTime = calculateVideoTimeUpdate(
                  metadata.mediaTime,
                  actualStart,
                  actualEnd,
                  frameInterval,
                );
                if (fixTime !== null) {
                  seek(fixTime);
                }
                if (videoUnplayable) {
                  video.pause();
                  if (
                    nextFrameCallbackRef !== null &&
                    'cancelVideoFrameCallback' in HTMLVideoElement.prototype
                  ) {
                    video.cancelVideoFrameCallback(nextFrameCallbackRef);
                  }
                  nextFrameCallbackRef = null;
                  return;
                }
                if (video && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
                  nextFrameCallbackRef = video.requestVideoFrameCallback(frameCallback);
                }
              };

              if (video && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
                nextFrameCallbackRef = video.requestVideoFrameCallback(frameCallback);
              }
            });
          }

          const p = new Promise<void>((resolve, reject) => {
            signal.onabort = () => resolve();

            hls!.once(Hls.Events.ERROR, (event, data) => {
              // Enhanced error logging with more context
              console.error('HLS error:', {
                type: data.type,
                details: data.details,
                fatal: data.fatal,
                url: data.url,
                response: data.response,
                error: data.error,
                event: event,
              });

              // Log video state for debugging
              logVideoState(video, src, 'HLS error occurred', hls);

              // Check for 401 authentication errors (expired token)
              const is401Error =
                data.type === Hls.ErrorTypes.NETWORK_ERROR && data.response?.code === 401;

              // Handle specific error types with different strategies
              const isBufferError =
                data.type === Hls.ErrorTypes.MEDIA_ERROR &&
                data.details === Hls.ErrorDetails.BUFFER_APPEND_ERROR;
              const isNetworkError = data.type === Hls.ErrorTypes.NETWORK_ERROR;
              const isFatalError = data.fatal;

              // Circuit breaker logic
              const circuitKey = `${src}-${data.type}-${data.details}`;
              const circuitBreakerResult = checkAndUpdateCircuitBreaker(circuitKey);

              if (!circuitBreakerResult.shouldProceed) {
                dispatch({ type: 'ERROR' });
                reject(new Error(`Circuit breaker is open for ${circuitKey}`));
                return;
              }

              // Helper function to attempt retry or handle max retries reached
              const attemptRetryOrFail = (reason?: string) => {
                if (retryCountRef.current < maxRetries) {
                  retryCountRef.current++;
                  if (reason) {
                    console.log(
                      `Retrying HLS load: ${reason} (attempt ${retryCountRef.current}/${maxRetries})`,
                    );
                  }
                  retryHlsLoad();
                } else {
                  handleMaxRetriesReached();
                }
              };

              // Helper to handle recovery result (both sync and async)
              const handleRecoveryResultWrapper = (
                result: boolean | Promise<boolean>,
                onSuccess: () => void,
                onFailure: () => void,
              ) => {
                const handleResult = (success: boolean) => {
                  if (success) {
                    onSuccess();
                  } else {
                    onFailure();
                  }
                };

                if (result instanceof Promise) {
                  result.then(handleResult).catch((error) => {
                    console.warn('Recovery promise rejected:', error);
                    handleResult(false);
                  });
                } else {
                  handleResult(result);
                }
              };

              if (isBufferError) {
                console.warn('Buffer append error detected, attempting recovery...');

                // For buffer errors, try to recover by clearing the buffer and seeking
                if (video && hls) {
                  const recoveryResult = attemptBufferRecovery(video, hls);

                  handleRecoveryResultWrapper(
                    recoveryResult,
                    () => {
                      // Recovery successful - check if video is actually working after a delay
                      setTimeout(() => {
                        if (video.readyState < 2) {
                          // HAVE_CURRENT_DATA - still having issues
                          console.log('Recovery did not resolve the issue, proceeding with retry');
                          attemptRetryOrFail('buffer error after recovery');
                        } else {
                          console.log('Buffer recovery successful, continuing playback');
                        }
                      }, 1000); // Give recovery time to work
                    },
                    () => {
                      // Recovery failed - fall back to retry
                      console.warn('Buffer recovery failed, falling back to retry');
                      attemptRetryOrFail('buffer recovery failed');
                    },
                  );

                  return; // Don't proceed with normal retry logic immediately
                }
              }

              // Handle 401 errors by refreshing the token and retrying
              if (is401Error && libraryId) {
                console.warn('Token expired (401), refreshing...');

                libraryPreview
                  .refreshPreviewToken(libraryId)
                  .then(() => {
                    console.log('Token refreshed, retrying...');
                    attemptRetryOrFail('token refreshed after 401');
                  })
                  .catch((error) => {
                    console.error('Failed to refresh token:', error);
                    dispatch({ type: 'ERROR' });
                    reject(new Error('Authentication failed'));
                  });

                return;
              }

              attemptRetryOrFail();

              function retryHlsLoad() {
                // Clean up current HLS instance
                if (hls) {
                  hls.stopLoad();
                  hls.detachMedia();
                  hls.destroy();
                }

                // For persistent buffer errors, try more aggressive recovery
                if (isBufferError && retryCountRef.current >= 2 && video) {
                  console.warn('Attempting aggressive recovery: recreating video element');

                  // Try to recreate the video element
                  try {
                    const parentElement = video.parentElement;
                    if (parentElement) {
                      const newVideo = video.cloneNode(false) as HTMLVideoElement;
                      newVideo.src = '';
                      newVideo.load();

                      // Replace the old video element
                      parentElement.replaceChild(newVideo, video);

                      // Update the video reference
                      setVideoRefWithForwarding(newVideo);

                      console.log('Video element recreated successfully');
                    }
                  } catch (recreateError) {
                    console.warn('Failed to recreate video element:', recreateError);
                  }
                }

                // Create new HLS instance for retry with adjusted settings for buffer errors
                const adjustedMaxBufferLength = isBufferError
                  ? Math.min(maxMaxBufferLength || DEFAULT_MAX_MAX_BUFFER_LENGTH, 10) // Even smaller buffer for retries
                  : maxMaxBufferLength;

                hls = getHlsInstance(
                  actualStart,
                  timeRange,
                  adjustedMaxBufferLength,
                  src,
                  accessToken,
                ) as Hls;

                // Retry after delay (longer delay for buffer errors)
                const retryDelayMs = isBufferError ? retryDelay * 2 : retryDelay;
                setTimeout(() => {
                  attemptHlsLoad(signal).then(resolve).catch(reject);
                }, retryDelayMs);
              }

              function handleMaxRetriesReached() {
                console.error('HLS failed after all retries');

                // Log final error state for debugging
                logVideoState(video, src, 'Final error state', hls, circuitKey);

                // Try one final fallback: attempt to load the video directly without HLS
                if (isBufferError && src && !src.includes('.m3u8') && video) {
                  console.warn('Attempting final fallback: direct video load');
                  try {
                    video.src = src;
                    video.load();
                    if (videoShouldAutoplayRef.current) {
                      video.play().catch(() => {
                        console.warn('Direct video load also failed');
                      });
                    }
                  } catch (fallbackError) {
                    console.warn('Fallback video load failed:', fallbackError);
                  }
                }
                // Only show error if this is not a rapid toggle situation
                // (i.e., if the component is still mounted and not being recreated)
                if (!abortController.signal.aborted) {
                  // Clear any existing timeout
                  if (errorTimeoutRef.current) {
                    clearTimeout(errorTimeoutRef.current);
                  }
                  // Show error after a short delay to avoid flickering during rapid toggles
                  errorTimeoutRef.current = setTimeout(() => {
                    dispatch({ type: 'ERROR' });
                  }, 500);
                }
                reject(new Error('HLS failed after all retries'));
              }
            });

            // wait for the first frame to be loaded
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
            video.addEventListener('loadeddata', () => {
              dispatch({ type: 'LOADING_COMPLETE' });
              resolve();
            });
          });

          hls!.loadSource(src);
          hls!.attachMedia(video);

          await p;
        };

        // Initialize HLS.js when supported.
        const useHlsJs = isHlsSupported();
        if (useHlsJs) {
          loadQueue
            .add(
              async ({ signal }) => {
                try {
                  await attemptHlsLoad(signal!);
                } catch (error) {
                  console.warn('HLS loading failed:', error);
                  // Keep loading state true even on failure
                }
              },
              { priority, signal: abortController.signal },
            )
            .catch((e) => console.warn('Aborting HLS load', e));
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS fallback (e.g. Safari when isHlsSupported() is false).
          // Backend accepts token-in-URL: append access_token so playlist + segment requests work.
          dispatch({ type: 'START_LOADING' });

          const setNativeHlsSourceAndFrameCallback = (urlToUse: string) => {
            video.src = urlToUse;

            // Set up frame callback for Safari native HLS
            if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
              if (frameCallbackTimer) {
                clearTimeout(frameCallbackTimer);
                frameCallbackTimer = null;
              }

              frameCallbackTimer = setTimeout(async () => {
                if (video && videoShouldAutoplayRef.current) {
                  try {
                    await video.play();
                  } catch (error) {
                    console.warn('Failed to play video for frame callback initialization:', error);
                  }
                }
                frameCallbackTimer = null;
              }, 100);

              const frameCallback = (now: number, metadata: VideoFrameCallbackMetadata) => {
                // Frame callback fired - clear the timer
                if (frameCallbackTimer) {
                  clearTimeout(frameCallbackTimer);
                  frameCallbackTimer = null;
                }

                updateProgress(metadata.mediaTime);
                const fixTime = calculateVideoTimeUpdate(
                  metadata.mediaTime,
                  actualStart,
                  actualEnd,
                  frameInterval,
                );
                if (fixTime !== null) {
                  seek(fixTime);
                }
                if (videoUnplayable) {
                  video.pause();
                  nextFrameCallbackRef = null;
                  return;
                }
                nextFrameCallbackRef = video.requestVideoFrameCallback(frameCallback);
              };

              video.requestVideoFrameCallback(frameCallback);
            } else {
              const animationCallback = () => {
                updateProgress(video.currentTime);
                const fixTime = calculateVideoTimeUpdate(
                  video.currentTime,
                  actualStart,
                  actualEnd,
                  frameInterval,
                );
                if (fixTime !== null) seek(fixTime);
                if (videoUnplayable) {
                  video.pause();
                  nextFrameCallbackRef = null;
                  return;
                }
                nextFrameCallbackRef = window.requestAnimationFrame(animationCallback);
              };
              window.requestAnimationFrame(animationCallback);
            }
          };

          // Prefer prop over URL extraction for reliability
          const libraryId = libraryIdProp || getLibraryIdFromPreviewUrl(src);

          if (libraryId) {
            libraryPreview
              .getValidPreviewToken(libraryId)
              .then((token) => {
                if (abortController.signal.aborted) return;
                const separator = src.includes('?') ? '&' : '?';
                const srcWithToken = `${src}${separator}access_token=${encodeURIComponent(token)}`;
                setNativeHlsSourceAndFrameCallback(srcWithToken);
              })
              .catch((error) => {
                console.error('Failed to get preview token for native HLS fallback:', error);
                dispatch({ type: 'ERROR' });
              });
          } else {
            console.warn(
              '[VideoPreview] Could not determine libraryId for native HLS (Safari). ' +
                'Pass libraryId as a prop or ensure the URL contains a valid library path. ' +
                'Attempting to load without token, which may result in 401 errors.',
              { src },
            );
            setNativeHlsSourceAndFrameCallback(src);
          }
        }
      }; // end initHls

      // Delay HLS initialization to avoid wasted loads during fast scrolling
      if (loadDelay > 0) {
        loadDelayTimer = setTimeout(initHls, loadDelay);
      } else {
        initHls();
      }

      return () => {
        if (loadDelayTimer) clearTimeout(loadDelayTimer);
        abortController.abort('VideoPreview changed props');
        if (hls) destroyHlsIntance(hls);
        if (nextFrameCallbackRef !== null) cancelFrameCall(nextFrameCallbackRef, video);
        // Clear frame callback timer
        if (frameCallbackTimer) {
          clearTimeout(frameCallbackTimer);
          frameCallbackTimer = null;
        }
        // Clear any pending error timeout
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }
      };
    }, [
      src,
      start,
      end,
      video,
      updateProgress,
      timeRange,
      actualStart,
      videoState.retryCount,
      autoPlay,
      loadDelay,
      offscreenDestroyed,
    ]);

    const handleMouseEnter = () => {
      dispatch({ type: 'SET_MOUSE_OVER', isMouseOver: true });
    };

    const handleMouseLeave = () => {
      dispatch({ type: 'SET_MOUSE_OVER', isMouseOver: false });
    };

    const handleRetry = useCallback(() => {
      dispatch({ type: 'RESET' });
    }, []);

    // Handle hover-based play/pause for videos that support it
    useEffect(() => {
      if (!video || !shouldUseHoverControl || !isAutoplayOnHoverEnabled || videoUnplayable) return;

      const handleMouseEnter = () => {
        dispatch({ type: 'SET_MOUSE_OVER', isMouseOver: true });
        if (video.paused) {
          dispatch({ type: 'START_HOVER_PLAY' });
          video.play().catch(console.error);
        }
      };

      const handleMouseLeave = () => {
        dispatch({ type: 'SET_MOUSE_OVER', isMouseOver: false });
        // Always pause on leave — handles PLAYING_HOVER, BUFFERING, and any other active state
        if (!video.paused) {
          dispatch({ type: 'PAUSE' });
          video.pause();
        }
      };

      const handleTap = () => {
        if (video.paused) {
          dispatch({ type: 'START_HOVER_PLAY' });
          video.play().catch(console.error);
        } else {
          dispatch({ type: 'PAUSE' });
          video.pause();
        }
      };

      const videoContainer = video.parentElement;
      if (!videoContainer) return;

      videoContainer.addEventListener('mouseenter', handleMouseEnter);
      videoContainer.addEventListener('mouseleave', handleMouseLeave);
      videoContainer.addEventListener('touchstart', handleTap, { passive: true });

      return () => {
        videoContainer.removeEventListener('mouseenter', handleMouseEnter);
        videoContainer.removeEventListener('mouseleave', handleMouseLeave);
        videoContainer.removeEventListener('touchstart', handleTap);
      };
    }, [video, shouldUseHoverControl, isAutoplayOnHoverEnabled, videoUnplayable]);

    // Render the video element if anything needs it: moments always, or if
    // the caller requested autoplay or external play control.
    const shouldRenderVideoElement = isMoment || autoPlay || externallyPlaying;
    // Controls the thumbnail overlay: when true, thumbnail blurs behind the video;
    // when false, thumbnail stays in front. Only relevant for callers that pass a
    // thumbnail (library cards). Moments don't pass a thumbnail, so the overlay is
    // empty and transparent — this flag has no visual effect for them.
    const shouldShowVideoOverThumbnail = externallyPlaying || (videoState.isMouseOver && autoPlay);

    return (
      <div
        ref={containerRef}
        style={style}
        className={classNames(css.root, className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Skeleton
          variant="rectangular"
          width={'100%'}
          height={'100%'}
          style={{ position: 'absolute', top: 0, left: 0 }}
          animation={
            videoState.state === 'PLAYING_HOVER' || videoState.state === 'PLAYING_AUTOPLAY'
              ? false
              : 'pulse'
          }
        />
        {shouldRenderVideoElement && (
          <video
            ref={setVideoRefWithForwarding}
            className={classNames(videoClassName, {
              [css.paused]: isPausedState && !isMoment,
            })}
            style={videoStyle}
            muted={shouldMute}
            playsInline
            controls={controls}
            onClick={onClick}
            disablePictureInPicture
            preload="metadata"
          />
        )}
        <div className={classNames(css.children)}>{children}</div>
        {shouldRenderVideoElement && (
          <VideoPreviewProgressBar
            progressBarRef={progressBarRef}
            className={classNames(css.progressBarContainer)}
          />
        )}
        {/* Always show thumbnail overlay, use z-index to control visibility */}
        <div
          className={classNames(css.overlay, {
            [css.overlayInFront]:
              !shouldRenderVideoElement ||
              (isPausedState && showThumbnailWhenPaused) ||
              !shouldShowVideoOverThumbnail,
            [css.overlayBlurred]:
              shouldRenderVideoElement &&
              !(isPausedState && showThumbnailWhenPaused) &&
              shouldShowVideoOverThumbnail,
          })}
        >
          {thumbnail && (
            <AuthenticatedImage
              className={classNames(css.thumbnail)}
              src={thumbnail}
              token={thumbnailToken}
              alt={''}
            />
          )}
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) onClick();
            }}
          />
        </div>
        {(videoState.state === 'LOADING' || videoState.state === 'BUFFERING') &&
          shouldShowVideoOverThumbnail && (
            <div className={classNames(css.loadingOverlay)}>
              <CircularProgress size={24} sx={{ zIndex: 5 }} />
            </div>
          )}
        {videoState.state === 'ERROR' && (
          <Tooltip title="Click to retry loading." placement="top">
            <div className={classNames(css.errorOverlay)} onClick={handleRetry}>
              <span className={classNames(css.errorIcon)}>❌</span>
            </div>
          </Tooltip>
        )}
      </div>
    );
  }),
);

type VideoPreviewProgressBarProps = {
  progressBarRef: React.RefObject<HTMLDivElement | null>;
  className?: string;
};

const VideoPreviewProgressBar = ({ progressBarRef, className }: VideoPreviewProgressBarProps) => {
  if (!progressBarRef) {
    console.warn('progressBarRef is null');
    return null;
  }

  return (
    <div className={classNames(className)}>
      <div ref={progressBarRef} className={classNames(css.progressBar)} />
    </div>
  );
};

function cancelFrameCall(frameCallbackRef: number, video: HTMLVideoElement) {
  if ('cancelVideoFrameCallback' in HTMLVideoElement.prototype) {
    video.cancelVideoFrameCallback(frameCallbackRef);
  } else {
    window.cancelAnimationFrame(frameCallbackRef);
  }
}

function destroyHlsIntance(hls: Hls) {
  hls.stopLoad();
  hls.detachMedia();
  hls.destroy();
}

/**
 * Attempts to recover from buffer append errors using multiple strategies
 */
/**
 * Logs comprehensive video and buffer state information for debugging
 */
function logVideoState(
  video: HTMLVideoElement | null | undefined,
  src: string,
  prefix: string = '',
  hls?: Hls | null,
  circuitKey?: string,
): void {
  const prefixStr = prefix ? `${prefix}: ` : '';
  console.error(`${prefixStr}Video state:`, {
    videoSrc: src,
    videoCurrentTime: video?.currentTime,
    videoReadyState: video?.readyState,
    videoNetworkState: video?.networkState,
    videoError: video?.error,
    hlsState: hls?.media?.readyState,
    circuitBreakerState: circuitKey ? circuitBreaker.get(circuitKey) : undefined,
    bufferInfo: video
      ? {
          buffered: Array.from({ length: video.buffered.length }, (_, i) => ({
            start: video.buffered.start(i),
            end: video.buffered.end(i),
          })),
          duration: video.duration,
        }
      : null,
  });
}

/**
 * Circuit breaker state and logic
 */
interface CircuitBreakerResult {
  shouldProceed: boolean;
  circuitState: { failures: number; lastFailure: number; isOpen: boolean };
}

/**
 * Checks circuit breaker state and updates it based on failure
 */
function checkAndUpdateCircuitBreaker(
  circuitKey: string,
  isFailed: boolean = true,
): CircuitBreakerResult {
  const now = Date.now();
  const circuitState = circuitBreaker.get(circuitKey) || {
    failures: 0,
    lastFailure: 0,
    isOpen: false,
  };

  // Check if circuit should be reset
  if (circuitState.isOpen && now - circuitState.lastFailure > CIRCUIT_BREAKER_TIMEOUT) {
    circuitState.isOpen = false;
    circuitState.failures = 0;
    console.log(`Circuit breaker reset for ${circuitKey}`);
  }

  // Check if circuit is open
  if (circuitState.isOpen) {
    console.warn(`Circuit breaker is open for ${circuitKey}, skipping retry`);
    return { shouldProceed: false, circuitState };
  }

  // Update circuit breaker state if this is a failure
  if (isFailed) {
    circuitState.failures++;
    circuitState.lastFailure = now;
    if (circuitState.failures >= CIRCUIT_BREAKER_THRESHOLD) {
      circuitState.isOpen = true;
      console.warn(
        `Circuit breaker opened for ${circuitKey} after ${circuitState.failures} failures`,
      );
    }
    circuitBreaker.set(circuitKey, circuitState);
  }

  return { shouldProceed: true, circuitState };
}

/**
 * Attempts to recover from buffer append errors using multiple strategies
 */
function attemptBufferRecovery(video: HTMLVideoElement, hls: Hls): boolean | Promise<boolean> {
  try {
    if (!video || !hls || !hls.media) {
      console.warn('Buffer recovery: Missing video, hls, or media element');
      return false;
    }

    const sourceBuffers = (hls.media as any).sourceBuffers;
    if (!sourceBuffers || sourceBuffers.length === 0) {
      console.warn('Buffer recovery: No source buffers available');
      return false;
    }

    const sourceBuffer = sourceBuffers[0];
    if (!sourceBuffer) {
      console.warn('Buffer recovery: No source buffer found');
      return false;
    }

    const currentTime = video.currentTime;
    const duration = video.duration || 0;

    console.log(`Buffer recovery: Starting recovery at time ${currentTime}, duration: ${duration}`);
    console.log(`Buffer recovery: Source buffer updating: ${sourceBuffer.updating}`);
    console.log(`Buffer recovery: Buffered ranges: ${sourceBuffer.buffered.length}`);

    // Strategy 1: Try to clear and rebuild buffer
    if (!sourceBuffer.updating && sourceBuffer.buffered.length > 0) {
      try {
        const start = sourceBuffer.buffered.start(0);
        const end = sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1);

        console.log(`Buffer recovery: Clearing buffer from ${start} to ${end}`);
        sourceBuffer.remove(start, end);

        // Wait for remove to complete
        return new Promise<boolean>((resolve) => {
          const onUpdateEnd = () => {
            sourceBuffer.removeEventListener('updateend', onUpdateEnd);
            console.log('Buffer recovery: Buffer cleared, seeking to trigger rebuffering');
            video.currentTime = currentTime;
            resolve(true);
          };

          const onError = () => {
            sourceBuffer.removeEventListener('updateend', onUpdateEnd);
            sourceBuffer.removeEventListener('error', onError);
            console.warn('Buffer recovery: Error during buffer removal');
            resolve(false);
          };

          sourceBuffer.addEventListener('updateend', onUpdateEnd);
          sourceBuffer.addEventListener('error', onError);

          // Timeout after 2 seconds
          setTimeout(() => {
            sourceBuffer.removeEventListener('updateend', onUpdateEnd);
            sourceBuffer.removeEventListener('error', onError);
            console.warn('Buffer recovery: Timeout during buffer removal');
            resolve(false);
          }, 2000);
        });
      } catch (removeError) {
        console.warn('Buffer recovery: Error during buffer removal:', removeError);
      }
    }

    // Strategy 2: Force seek to trigger rebuffering
    try {
      console.log('Buffer recovery: Attempting forced seek');
      video.currentTime = currentTime;

      // Strategy 3: Try to recover using HLS.js built-in recovery
      if (hls.recoverMediaError) {
        console.log('Buffer recovery: Attempting HLS.js media error recovery');
        hls.recoverMediaError();
      }

      return true;
    } catch (seekError) {
      console.warn('Buffer recovery: Error during seek:', seekError);
      return false;
    }
  } catch (error) {
    console.error('Buffer recovery: Unexpected error:', error);
    return false;
  }
}

/**
 * Creates an HLS.js instance configured for video preview playback.
 *
 * @param accessToken - Can be string (static), function (dynamic getter), or null.
 *                      Use function for token rotation: () => libraryPreview.getCachedPreviewToken(id)
 */
function getHlsInstance(
  start: number,
  timeRange: number,
  maxMaxBufferLength?: number,
  sourceUrl?: string,
  accessToken?: string | null | (() => string | null),
): Hls | null {
  // Use provided access token or extract from URL as fallback for backward compatibility
  const token = accessToken || (sourceUrl ? getAccessTokenFromUrl(sourceUrl) : null);

  return new Hls({
    startPosition: start,
    maxBufferLength: Math.min(Math.max((timeRange + 1) / 2, 2), 10),
    maxMaxBufferLength: maxMaxBufferLength || DEFAULT_MAX_MAX_BUFFER_LENGTH,
    maxBufferSize: 60 * MB_TO_BYTES, // 60MB buffer size limit
    maxBufferHole: 0.1, // Smaller buffer hole tolerance
    testBandwidth: false,
    startLevel: 0,
    progressive: true,
    maxLoadingDelay: 2000,
    lowLatencyMode: false,
    backBufferLength: Math.max(timeRange, 5),
    // Additional buffer management options
    maxStarvationDelay: 2000,
    // Network and loading options
    manifestLoadingTimeOut: 10000,
    manifestLoadingMaxRetry: 3,
    levelLoadingTimeOut: 10000,
    levelLoadingMaxRetry: 3,
    fragLoadingTimeOut: 20000,
    fragLoadingMaxRetry: 3,
    // Additional options to prevent buffer issues
    enableWorker: true,
    enableSoftwareAES: true,
    debug: false,
    fetchSetup: hlsFetchSetup(token),
  });
}

/**
 * Calculates the time to seek to, based on the media time, current time, and the actual start and end times.
 * Makes sure that the video always stays inbouinds, and doesn't go over the boundaries.
 * @param mediaTime - The time of the media. (from video frame callback or raf video.currentTime)
 * @param actualStart - The actual start time of the video.
 * @param actualEnd - The actual end time of the video.
 * @returns The time to seek to, or null if no seeking is needed.
 */
function calculateVideoTimeUpdate(
  mediaTime: number,
  actualStart: number,
  actualEnd: number,
  safeOffset: number = 0.01,
) {
  if (mediaTime + safeOffset >= actualEnd || mediaTime + safeOffset < actualStart) {
    return actualStart;
  }
  return null;
}
